// #31 Chip and Chips. A chip is a label (a skill, an artifact) or, with onToggle, a checkbox (role="checkbox",
// aria-checked; selected = .mc-on with a check). A filter chip reads *Label: value* and carries a remove IconButton.
// Chips lays chips in a wrapping row, labelled when it is a group; `set` stacks several labelled rows (chipset).
// DC-24: can={false} draws nothing; blocked (a toggle chip, or a filter chip's remove) is dashed, aria-disabled and
// described by its reason, with the mirrored tooltip; a fixed toggle chip takes its reason through blocked; mode="view"
// draws the words alone. A filter chip's remove is described by the chip's own words, so each Clear says which filter.
// A task chip (room · Teams link · travel) takes `done`: filled green with a check and *Completed* for a screen reader
// when done, the neutral chip when open. `val` stays the validated-value (ROOTS) chip and is never borrowed for a task.

import { useId, type ReactNode } from "react";
import { CORE_LABELS } from "./labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Icon, type IconName } from "./icons";
import { Tooltip } from "../lib/Tooltip";
import { IconButton, useBlocked, type Blocked } from "./Button";
import { SCHEDULING_STATUS } from "../labels";
import { VisuallyHidden } from "../lib/VisuallyHidden";

export interface ChipProps {
  children: ReactNode;
  /** Selected (a toggle chip's checked state, or a chosen filter). */
  on?: boolean | undefined;
  /** A validated value (green edge). */
  val?: boolean | undefined;
  /** A task chip: true = done (green fill, a check, *Completed* read after the words); false = open (neutral). */
  done?: boolean | undefined;
  sm?: boolean | undefined;
  /** Wraps long text onto several lines. */
  multi?: boolean | undefined;
  icon?: IconName | undefined;
  /** Makes the chip a checkbox; receives the next checked state. */
  onToggle?: ((on: boolean) => void) | undefined;
  /** A filter chip: its field name, drawn as "name: value". */
  name?: ReactNode | undefined;
  /** A filter chip's remove action (an IconButton named *Clear*). */
  onRemove?: (() => void) | undefined;
  removeLabel?: string | undefined;
  /** A toggle chip that cannot change (aria-disabled); its reason comes through `blocked`. */
  fixed?: boolean | undefined;
  can?: boolean | undefined;
  /** DC-24: the rule that blocks the toggle or the remove. */
  blocked?: Blocked | undefined;
  /** "view": the chip's words only, no control. */
  mode?: "edit" | "view" | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function Chip({
  children, on, val, done, sm, multi, icon, onToggle, name, onRemove, removeLabel = CORE_LABELS.clear, fixed, can, blocked, mode, hs, className,
}: ChipProps) {
  const hot = useHs(hs);
  const wordsId = useId();
  const view = mode === "view";
  const block = useBlocked(onToggle && !view ? blocked : undefined);
  if (can === false) return null;
  const toggles = Boolean(onToggle) && !view;
  const removes = Boolean(onRemove) && !view;
  const classes = cx(
    "mc-chip", on && "mc-on", val && "mc-val", sm && "mc-chip-sm", done && "mc-chip-done", multi && "mc-chip-multi", fixed && toggles && "mc-chip-fixed",
    blocked && (toggles || removes) && "mc-dis", className,
  );
  const lead = icon ? <Icon name={icon} size={14} /> : on || done ? <Icon name="check" size={14} /> : null;
  const words = name !== undefined ? <><span className="mc-chip-n">{name}:</span> <b className="mc-chip-v">{children}</b></> : children;
  const text = done ? <>{words}<VisuallyHidden>{`, ${SCHEDULING_STATUS.completed}`}</VisuallyHidden></> : words;
  if (toggles && onToggle) {
    const button = (
      <button
        type="button"
        role="checkbox"
        aria-checked={Boolean(on)}
        aria-disabled={fixed || blocked ? true : undefined}
        aria-describedby={block.describedBy}
        className={classes}
        onClick={() => { if (!fixed && !blocked) onToggle(!on); }}
        {...hot}
      >
        {lead}{text}
      </button>
    );
    if (!blocked) return button;
    return (
      <>
        <Tooltip label={blocked.reason} mode="mirror">{button}</Tooltip>
        {block.reason}
      </>
    );
  }
  return (
    <span className={classes} {...(removes || view ? {} : hot)}>
      {lead}
      {removes ? <span className="mc-chip-w" id={wordsId}>{text}</span> : text}
      {removes ? (
        <IconButton icon="x" label={removeLabel} sm className="mc-chip-x" onClick={onRemove} blocked={blocked} aria-describedby={wordsId} hs={hs} />
      ) : null}
    </span>
  );
}

export interface ChipsProps {
  children: ReactNode;
  /** The group's label: shown before the chips, and naming the group. */
  label?: ReactNode | undefined;
  /** A column of labelled chip rows (each child a Chips). */
  set?: boolean | undefined;
  className?: string | undefined;
}

export function Chips({ children, label, set, className }: ChipsProps) {
  const id = useId();
  if (set) return <div className={cx("mc-chipset", className)}>{children}</div>;
  return (
    <div className={cx("mc-chips", className)} role={label ? "group" : undefined} aria-labelledby={label ? id : undefined}>
      {label ? <span className="mc-chips-l" id={id}>{label}</span> : null}
      {children}
    </div>
  );
}
